import React, { useState, useEffect, useRef } from 'react';
import { TableSkeleton } from "../../components/SkeletonLoading/PageSkeletons";
import { authService } from '../../services/api';
import { fmtDateTimezone } from '../utils/dateUtils/fmtDate';
import { useAuth } from '../../context/AuthProvider';
import FiltersSection from '../../components/Filter/Filter';
import '../../App.css';
import { getVisibleBots } from '../../utils/bots';
import { useBotPhotosByUsername } from '../../hooks/useBotPhotos';
import BotChip from '../../components/BotChip/BotChip';
import StatusPill from '../../components/StatusPill/StatusPill';

export default function Leads() {
    const { user } = useAuth();
    const [summary, setSummary] = useState(null);
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    // const [summaryLoading, setSummaryLoading] = useState(true);

    // Filtros gerenciados pelo FiltersSection
    const [filters, setFilters] = useState({
        search: '',
        status: '',
        botIds: [],
        dateFilter: 'tudo',
        fluxo: 'todos',
        starts: 'todos',
        startDate: null,
        endDate: null
    });

    // Paginação
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState(null);
    const [includePhotos, setIncludePhotos] = useState(true);
    // Descarta respostas atrasadas (fotos de uma página/filtro anterior).
    const loadSeqRef = useRef(0);

    const userBots = getVisibleBots(user?.bots);
    const photoByUsername = useBotPhotosByUsername(userBots);

    // // Carregar resumo
    // useEffect(() => {
    //     loadSummary();
    // }, []);

    // Carregar leads quando filtros mudarem
    useEffect(() => {
        loadLeads();
    }, [currentPage, filters]);

    const handleSearchChange = (value) => {
        setFilters(prev => {
            if (prev.search === value) return prev;
            return { ...prev, search: value };
        });
        setCurrentPage(1);
    };

    // const loadSummary = async () => {
    //     try {
    //         setSummaryLoading(true);
    //         const response = await authService.leads.summary();
    //         if (response.data.success) {
    //             setSummary(response.data.data);
    //         }
    //     } catch (error) {
    //         console.error('Erro ao carregar resumo:', error);
    //     } finally {
    //         setSummaryLoading(false);
    //     }
    // };

    const loadLeads = async () => {
        const seq = ++loadSeqRef.current;
        try {
            setLoading(true);
            const params = {
                page: currentPage,
                per_page: 20,
                // Fotos vêm depois (loadPhotos): dependem do Telegram e
                // seguravam a lista por 10s+.
                include_photos: false
            };

            if (filters.search) params.search = filters.search;
            if (filters.status) params.status = filters.status;
            if (filters.botIds && filters.botIds.length > 0) params.bot_ids = filters.botIds.join(',');
            if (filters.dateFilter && filters.dateFilter !== 'tudo') params.date_filter = filters.dateFilter;
            if (filters.fluxo && filters.fluxo !== 'todos') params.fluxo = filters.fluxo;
            if (filters.starts && filters.starts !== 'todos') params.starts = parseInt(filters.starts);

            const response = await authService.leads.list(params);
            if (seq !== loadSeqRef.current) return;
            if (response.data.success) {
                setLeads(response.data.data.leads);
                if (includePhotos) loadPhotos(response.data.data.leads, seq);
                setPagination({
                    total: response.data.data.total,
                    totalIsApproximate: response.data.data.total_is_approximate,
                    page: response.data.data.page,
                    per_page: response.data.data.per_page,
                    total_pages: response.data.data.total_pages,
                    has_prev: response.data.data.page > 1,
                    has_next: response.data.data.page < response.data.data.total_pages
                });
            }
        } catch (error) {
            console.error('Erro ao carregar leads:', error);
        } finally {
            // Só a carga mais recente encerra o loading: se uma resposta
            // descartada desligasse, a tela piscava "Nenhum lead encontrado"
            // enquanto a carga atual ainda estava a caminho.
            if (seq === loadSeqRef.current) setLoading(false);
        }
    };

    const loadPhotos = async (pageLeads, seq) => {
        const items = pageLeads
            .filter(l => l.bot?.id)
            .map(l => ({ telegram_user_id: l.telegram_user_id, bot_id: l.bot.id }));
        if (items.length === 0) return;
        try {
            const { data } = await authService.leads.photos(items);
            if (seq !== loadSeqRef.current || !data?.success) return;
            const photos = data.data || {};
            setLeads(prev => prev.map(l => {
                const url = photos[String(l.telegram_user_id)];
                return url ? { ...l, profile_photo_url: url } : l;
            }));
        } catch (error) {
            // Sem foto: fica o avatar padrão.
            console.error('Erro ao carregar fotos dos leads:', error);
        }
    };

    const handleApplyFilters = (filterData) => {
        // Map period to dateFilter expected by backend
        const periodMap = {
            'today': 'hoje',
            'yesterday': 'ontem',
            '7days': 'semana',
            'thisMonth': 'mes',
            'lastMonth': 'mes',
            'max': 'tudo',
            'custom': 'tudo'
        };
        
        const dateFilter = filterData.period ? periodMap[filterData.period] || 'tudo' : 'tudo';
        
        setFilters(prev => ({
            ...prev,
            botIds: filterData.botIds || [],
            status: filterData.status || '',
            fluxo: filterData.fluxo || 'todos',
            starts: filterData.starts || 'todos',
            dateFilter: dateFilter
        }));
        setCurrentPage(1);
    };

    const handleNextPage = () => {
        if (pagination?.has_next) {
            setCurrentPage(prev => prev + 1);
        }
    };

    const handlePrevPage = () => {
        if (pagination?.has_prev) {
            setCurrentPage(prev => prev - 1);
        }
    };

    const getStatusLabel = (status) => {
        const labels = {
            'novo': 'Novo',
            'pendente': 'Pendente',
            'pago': 'Pago',
            'bloqueado': 'Bloqueado'
        };
        return labels[status] || status || '-';
    };

    const getStatusTextClass = (status) => {
        const classes = {
            'novo': 'text-blue-400',
            'pendente': 'text-yellow-400',
            'pago': 'text-green-400',
            'bloqueado': 'text-red-400'
        };
        return classes[status] || 'text-gray-300';
    };

    const getStatusBgClass = (status) => {
        const classes = {
            'novo': 'bg-blue-900',
            'pendente': 'bg-yellow-900',
            'pago': 'bg-green-900',
            'bloqueado': 'bg-red-900'
        };
        return classes[status] || 'bg-gray-900';
    };

    const getStatusBadge = (status) => (
        <div className={getStatusTextClass(status)}>
            <div className={getStatusBgClass(status) + " font-bold inline-block px-2 py-1 rounded text-xs font-semibold"}>
                {getStatusLabel(status)}
            </div>
        </div>
    );

    // Cor do StatusPill (card mobile)
    const getStatusColor = (status) => {
        const colors = {
            'novo': 'blue',
            'pendente': 'yellow',
            'pago': 'green',
            'bloqueado': 'red'
        };
        return colors[status] || 'gray';
    };

    // Lista normalizada de bots do lead (usada pelo mobile e por formatBots)
    const getBotList = (lead) =>
        Array.isArray(lead?.bots) && lead.bots.length > 0
            ? lead.bots
            : (lead?.bot ? [lead.bot] : []);

    // Foto do bot pelo username do lead (normaliza o @)
    const getBotPhoto = (bot) => {
        const username = (bot?.username || bot?.bot_username || '').replace(/^@/, '');
        return photoByUsername[username];
    };

    const getDefaultAvatar = () => {
        return "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 40 40'%3E%3Crect width='40' height='40' fill='%232b2d42'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='20' fill='%238b5cf6'%3E%3F%3C/text%3E%3C/svg%3E";
    };

    const formatCodigoVendas = (codigos, maxDisplay = 3) => {
        if (!codigos || codigos.length === 0) {
            return <span className="text-gray-500">-</span>;
        }

        const displayCodigos = codigos.slice(0, maxDisplay);
        const remaining = codigos.length - maxDisplay;

        return (
            <span className="text-sm" style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                {displayCodigos.map((codigo, idx) => (
                    <span key={idx} style={{
                        background: 'rgba(139, 92, 246, 0.1)',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontSize: '0.8rem'
                    }}>
                        {codigo.length > 10 ? `${codigo.substring(0, 10)}...` : codigo}
                    </span>
                ))}
                {remaining > 0 && (
                    <span style={{
                        color: 'var(--text-secondary)',
                        fontSize: '0.8rem',
                        fontStyle: 'italic'
                    }}>
                        +{remaining} mais
                    </span>
                )}
            </span>
        );
    };

    const formatBots = (lead, maxDisplay = 3) => {
        const botList = getBotList(lead);

        if (botList.length === 0) {
            return <span className="text-gray-500">-</span>;
        }

        const displayBots = botList.slice(0, maxDisplay);
        const remaining = botList.length - maxDisplay;

        return (
            <span className="text-sm" style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                {displayBots.map((bot, idx) => {
                    const label = bot?.username || bot?.name || `Bot ${bot?.id || ''}`.trim();

                    return (
                        <span key={`${bot?.id || 'bot'}-${idx}`} style={{
                            background: 'rgba(139, 92, 246, 0.1)',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontSize: '0.8rem',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px'
                        }}>
                           
                            <span>{label}</span>
                        </span>
                    );
                })}
                {remaining > 0 && (
                    <span style={{
                        color: 'var(--text-secondary)',
                        fontSize: '0.8rem',
                        fontStyle: 'italic'
                    }}>
                        +{remaining} mais
                    </span>
                )}
            </span>
        );
    };

    return (
        <main className="page">
            <div className="page-header-with-filters">
                <div className="page-header-content">
                    <h1 className='title'>Lista de Leads</h1>
                    <p>Gerencie seus leads e clientes</p>
                </div>
            </div>



            {/* Filtros */}
            <FiltersSection
                onApplyFilters={handleApplyFilters}
                bots={userBots}
                gateway={[]}
                showGatewayFilter={false}
                showStatusFilter={true}
                showFluxoFilter={true}
                showStartsFilter={true}
                enableBotMultiSelect={true}
                showInputFilter={true}
                onSearchChange={handleSearchChange}
                searchPlaceholder="Buscar por nome, sobrenome ou @username..."
            />
            <div className="stats-grid" style={{ marginBottom: '24px' }}>
                {/* {summaryLoading ? (
                    <Loading text="Carregando estatísticas..." />
                ) : (
                    <>
                        <StatCard
                            title="NOVOS"
                            value={fmtNumberBR.format(summary?.novos || 0)}
                            icon={{ class: "fas fa-user-plus", color: 'purple' }}
                            gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                        />
                        <StatCard
                            title="PENDENTES"
                            value={fmtNumberBR.format(summary?.pendentes || 0)}
                            icon={{ class: "fas fa-clock", color: 'orange' }}
                            gradient="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
                        />
                        <StatCard
                            title="PAGOS"
                            value={fmtNumberBR.format(summary?.pagos || 0)}
                            icon={{ class: "fas fa-dollar-sign", color: 'green' }}
                            gradient="linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)"
                        />
                        <StatCard
                            title="BLOQUEADOS"
                            value={fmtNumberBR.format(summary?.bloqueados || 0)}
                            icon={{ class: "fas fa-ban", color: 'red' }}
                            gradient="linear-gradient(135deg, #fa709a 0%, #fee140 100%)"
                        />
                        <StatCard
                            title="TOTAL LEADS"
                            value={fmtNumberBR.format(summary?.total || 0)}
                            icon={{ class: "fas fa-users", color: 'blue' }}
                            gradient="linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)"
                        />
                    </>
                )} */}
            </div>
            {/* Lista de Leads */}
            {loading ? (
                <div className="mt-6"><TableSkeleton label="Carregando leads" columns={["Lead", "ID", "Cód. Vendas", "Bot", "Fluxo", "Status", "Data de Cadastro"]} /></div>
            ) : (
                <div className="mt-6">
                    {leads.length === 0 && (
                        <div className="empty-state">
                            <i className="fas fa-users" />
                            <h4>Nenhum lead encontrado</h4>
                            <p>Quando leads interagirem com seus bots, eles aparecerão aqui.</p>
                        </div>
                    )}

                    {/* DESKTOP */}
                    {leads.length > 0 && (
                        <div className="sx-desktop spt-reveal">
                            <table className="sx-table">
                                <thead>
                                    <tr>
                                        <th>Lead</th>
                                        <th>ID</th>
                                        <th>Cód. Vendas</th>
                                        <th>Bot</th>
                                        <th>Fluxo</th>
                                        <th>Status</th>
                                        <th>Data de Cadastro</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {leads.map(lead => (
                                        <tr key={lead.telegram_user_id}>
                                            <td>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                    <img
                                                        src={lead.profile_photo_url || getDefaultAvatar()}
                                                        alt={lead.name}
                                                        style={{
                                                            width: '40px',
                                                            height: '40px',
                                                            borderRadius: '50%',
                                                            objectFit: 'cover',
                                                            border: '2px solid rgba(139, 92, 246, 0.3)'
                                                        }}
                                                        onError={(e) => {
                                                            e.target.src = getDefaultAvatar();
                                                        }}
                                                    />
                                                    <div>
                                                        <div style={{ fontWeight: '600' }}>
                                                            {lead.name}
                                                        </div>
                                                        {lead.telegram_username && (
                                                            <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                                                                @{lead.telegram_username}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td>#{lead.telegram_user_id}</td>
                                            <td>
                                                {formatCodigoVendas(lead.codigo_venda_ids)}
                                            </td>
                                            <td>
                                                {formatBots(lead)}
                                            </td>
                                            <td>
                                                <span style={{
                                                    padding: '4px 8px',
                                                    background: 'rgba(139, 92, 246, 0.1)',
                                                    borderRadius: '6px',
                                                    fontSize: '0.85rem'
                                                }}>
                                                    {lead.fluxo}
                                                </span>
                                            </td>
                                            <td>{getStatusBadge(lead.status)}</td>
                                            <td>{fmtDateTimezone(lead.created_at)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* MOBILE */}
                    <div className="sx-mobile spt-reveal">
                        {leads.map(lead => {
                            const botList = getBotList(lead);
                            return (
                                <div key={lead.telegram_user_id} className="sx-card">
                                    {/* Header: foto + nome/@user + status */}
                                    <div className="flex items-center justify-between gap-3 mb-3">
                                        <div className="flex items-center gap-3 min-w-0">
                                            <img
                                                src={lead.profile_photo_url || getDefaultAvatar()}
                                                alt={lead.name}
                                                style={{
                                                    width: '44px',
                                                    height: '44px',
                                                    borderRadius: '50%',
                                                    objectFit: 'cover',
                                                    border: '2px solid rgba(139, 92, 246, 0.3)',
                                                    flexShrink: 0
                                                }}
                                                onError={(e) => {
                                                    e.target.src = getDefaultAvatar();
                                                }}
                                            />
                                            <div className="min-w-0">
                                                <div className="font-semibold text-white truncate" title={lead.name}>
                                                    {lead.name}
                                                </div>
                                                {lead.telegram_username && (
                                                    <div className="text-xs truncate" style={{ color: 'var(--text-secondary)' }}>
                                                        @{lead.telegram_username}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <StatusPill color={getStatusColor(lead.status)}>
                                            {getStatusLabel(lead.status)}
                                        </StatusPill>
                                    </div>

                                    {/* Bots */}
                                    {botList.length > 0 && (
                                        <div className="flex flex-wrap gap-1.5 mb-3">
                                            {botList.slice(0, 3).map((bot, idx) => (
                                                <BotChip
                                                    key={`${bot?.id || 'bot'}-${idx}`}
                                                    photoUrl={getBotPhoto(bot)}
                                                    label={bot?.username || bot?.name || `Bot ${bot?.id || ''}`.trim()}
                                                />
                                            ))}
                                            {botList.length > 3 && (
                                                <span className="sx-pill sx-pill--gray">+{botList.length - 3}</span>
                                            )}
                                        </div>
                                    )}

                                    {/* ID + Fluxo + Vendas */}
                                    <div className="sx-stats mb-3">
                                        <div className="sx-stat">
                                            <div className="sx-stat-value">#{lead.telegram_user_id}</div>
                                            <div className="sx-stat-label">ID</div>
                                        </div>
                                        <div className="sx-stat">
                                            <div className="sx-stat-value" title={lead.fluxo || ''}>{lead.fluxo || '—'}</div>
                                            <div className="sx-stat-label">Fluxo</div>
                                        </div>
                                        <div className="sx-stat">
                                            <div className="sx-stat-value">{lead.codigo_venda_ids?.length || 0}</div>
                                            <div className="sx-stat-label">Cód. Vendas</div>
                                        </div>
                                    </div>

                                    {/* Códigos de venda */}
                                    {lead.codigo_venda_ids?.length > 0 && (
                                        <div className="mb-3">
                                            {formatCodigoVendas(lead.codigo_venda_ids, 2)}
                                        </div>
                                    )}

                                    <div className="sx-card-footer" style={{ marginBottom: 0 }}>
                                        {fmtDateTimezone(lead.created_at)}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* PAGINAÇÃO */}
                    {!loading && leads.length > 0 && pagination && (
                        <div className="mt-8 flex items-center justify-between">
                            <div className="text-sm text-gray-600">
                                Página {currentPage} de {pagination.total_pages} ({pagination.total}{pagination.totalIsApproximate ? '+' : ''} leads)
                            </div>

                            <div className="flex gap-2">
                                <button
                                    onClick={handlePrevPage}
                                    disabled={!pagination.has_prev}
                                    className="btn-secondary"
                                    style={{ opacity: pagination.has_prev ? 1 : 0.5 }}
                                >
                                    <i className="fas fa-chevron-left"></i> Anterior
                                </button>

                                <button
                                    onClick={handleNextPage}
                                    disabled={!pagination.has_next}
                                    className="btn-primary"
                                    style={{ opacity: pagination.has_next ? 1 : 0.5 }}
                                >
                                    Próximo <i className="fas fa-chevron-right"></i>
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
 
        </main >
    );
}